import React from 'react';
import { useTranslation } from 'react-i18next';
import { FormikProps, withFormik } from 'formik';
import styled from 'styled-components';

import { PasswordField } from '@/components/shared/PasswordField';
import { PasswordPolicy } from '@/components/shared/PasswordPolicy';
import * as constants from '@/constants';
import { passwordSchema } from '@/services/validators/password';
import { evaluatePasswordStrength } from '@/utils/evaluatePasswordStrength';
import { Label } from '@/components/shared/Label';
import { SharedButton } from '@/components/shared/Button';

const strengthMeter = evaluatePasswordStrength(constants.PASSWORD_PATTERN);

type OuterProps = {
  isLoading: boolean;
  mismatchError?: string;
  onSubmit: (currentPassword: string, newPassword: string) => void;
};

type Values = {
  currentPassword: string;
  newPassword: string;
};

export const PasswordEditForm = withFormik<OuterProps, Values>({
  mapPropsToValues: () => ({
    currentPassword: '',
    newPassword: '',
  }),
  validationSchema: passwordSchema,
  handleSubmit: (values, { props, setSubmitting }) => {
    if (props.onSubmit) {
      props.onSubmit(values.currentPassword, values.newPassword);
    }
    setSubmitting(false);
  },
})(
  // enhanced form
  ({
    errors,
    handleChange,
    handleBlur,
    handleSubmit,
    isLoading = false,
    mismatchError,
    touched,
    values,
  }: OuterProps & FormikProps<Values>) => {
    const { t } = useTranslation();

    return (
      <form onSubmit={handleSubmit}>
        <StyledPasswordField>
          <PasswordField
            autoComplete="new-password"
            dataTestId="current-password"
            id="currentPassword"
            label={t('ui.password.current.label')}
            withToggle={true}
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.currentPassword}
          />
          {mismatchError && (
            <div>
              <Label>{mismatchError}</Label>
            </div>
          )}
          {touched.currentPassword && errors.currentPassword && (
            <div>
              <Label>{errors.currentPassword}</Label>
            </div>
          )}
        </StyledPasswordField>
        <StyledPasswordField>
          <PasswordField
            autoComplete="new-password"
            dataTestId="new-password"
            id="newPassword"
            label={t('ui.password.new.label')}
            withToggle={true}
            onChange={handleChange}
            onBlur={handleBlur}
            strengthMeter={strengthMeter}
            value={values.newPassword}
          />
          {touched.newPassword && errors.newPassword && (
            <div>
              <Label>{errors.newPassword}</Label>
            </div>
          )}
        </StyledPasswordField>
        <PasswordPolicy />
        <StyledSubmitButton>
          <SharedButton
            dataTest="update-password"
            disabled={strengthMeter(values.newPassword) < 2 || isLoading}
            type="submit"
          >
            {t('ui.action.changeNow')}
          </SharedButton>
        </StyledSubmitButton>
      </form>
    );
  },
);

const StyledPasswordField = styled.div`
  margin-top: 1em;
`;

const StyledSubmitButton = styled.div`
  color: #bbb;
  text-align: center;
`;
